type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

const LOG_LEVELS: Record<LogLevel, number> = {
	debug: 0,
	info: 1,
	warn: 2,
	error: 3,
	silent: 4,
};

const COLOR_MAP: Record<LogLevel, { prefix: string; text: string }> = {
	debug: { prefix: 'color: #9E9E9E; font-weight: bold;', text: 'color: #9E9E9E;' },
	info: { prefix: 'background: #1565C0; color: #fff; font-weight: bold; padding: 1px 4px; border-radius: 2px;', text: 'color: inherit;' },
	warn: {
		prefix: 'background: #E65100; color: #fff; font-weight: bold; padding: 1px 4px; border-radius: 2px;',
		text: 'color: #FF9800; font-weight: bold;',
	},
	error: {
		prefix: 'background: #B71C1C; color: #fff; font-weight: bold; padding: 1px 4px; border-radius: 2px;',
		text: 'color: #F44336; font-weight: bold;',
	},
	silent: { prefix: '', text: '' },
};

export interface ILogger {
	setLevel(level: LogLevel): void;
	getLevel(): LogLevel;
	debug(...args: unknown[]): void;
	log(...args: unknown[]): void;
	info(...args: unknown[]): void;
	warn(...args: unknown[]): void;
	error(...args: unknown[]): void;
	group(...args: unknown[]): void;
	groupCollapsed(...args: unknown[]): void;
	groupEnd(): void;
}

class Logger implements ILogger {
	private currentLevel: LogLevel;
	private colorEnabled: boolean;

	constructor() {
		// Mặc định theo môi trường: production -> 'warn', else -> 'debug'
		const defaultLevel = process.env.NODE_ENV === 'production' ? 'warn' : 'debug';
		const envLevel = (process.env.REACT_APP_LOG_LEVEL || process.env.LOG_LEVEL || defaultLevel) as LogLevel;
		this.currentLevel = this.isValidLevel(envLevel) ? envLevel : defaultLevel;

		// NO_COLOR=1 hoặc rỗng trong môi trường không phải browser thì tắt màu
		const noColor = process.env.NO_COLOR || process.env.REACT_APP_NO_COLOR;
		this.colorEnabled = typeof window !== 'undefined' && !noColor;

		if (typeof window !== 'undefined') {
			window.__lg = this;
			window.setLogLevel = (level: LogLevel) => this.setLevel(level);
		}

		this.info(`[Logger] initialized at level: "${this.currentLevel}" (colors: ${this.colorEnabled})`);
	}

	private isValidLevel(level: string): level is LogLevel {
		return level in LOG_LEVELS;
	}

	setLevel(level: LogLevel): void {
		if (!this.isValidLevel(level)) {
			console.error(`[Logger] Invalid level: "${level}". Valid: ${Object.keys(LOG_LEVELS).join(', ')}`);
			return;
		}
		this.currentLevel = level;
		console.info(`[Logger] Log level changed to: "${level}"`);
	}

	getLevel(): LogLevel {
		return this.currentLevel;
	}

	private shouldLog(level: LogLevel): boolean {
		return LOG_LEVELS[level] >= LOG_LEVELS[this.currentLevel];
	}

	private format(level: LogLevel, args: unknown[]): unknown[] {
		const ts = new Date().toISOString();
		const prefix = `[${level.toUpperCase()}] ${ts}`;

		if (!this.colorEnabled || level === 'silent') {
			return [prefix, ...args];
		}

		const styles = COLOR_MAP[level];
		// Giữ nguyên từng arg để DevTools có thể expand object/array
		return [`%c${prefix}%c`, styles.prefix, 'color: inherit;', ...args];
	}

	debug(...args: unknown[]): void {
		if (this.shouldLog('debug')) {
			console.debug(...this.format('debug', args));
		}
	}

	log(...args: unknown[]): void {
		if (this.shouldLog('info')) {
			console.log(...this.format('info', args));
		}
	}

	info(...args: unknown[]): void {
		if (this.shouldLog('info')) {
			console.info(...this.format('info', args));
		}
	}

	warn(...args: unknown[]): void {
		if (this.shouldLog('warn')) {
			console.warn(...this.format('warn', args));
		}
	}

	error(...args: unknown[]): void {
		if (this.shouldLog('error')) {
			console.error(...this.format('error', args));
		}
	}

	group(...args: unknown[]): void {
		if (this.shouldLog('debug')) {
			console.group(...this.format('debug', args));
		}
	}

	groupCollapsed(...args: unknown[]): void {
		if (this.shouldLog('debug')) {
			console.groupCollapsed(...this.format('debug', args));
		}
	}

	groupEnd(): void {
		console.groupEnd();
	}
}

const logger = new Logger();
export default logger;
